import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Target, Trophy, Flame, Calendar, Award, Rocket, Medal, Zap, Users, Crown, Crosshair, LogOut, Trash2, AlertTriangle, Ban } from 'lucide-react'
import { mockCurrentUser, mockBadges, RANK_CONFIG } from '../data/mock'
import { apiUrl } from '../config/api'
import { sispoliBridge } from '../lib/sispoliBridge'
import Avatar from '../components/ui/Avatar'
import RankBadge from '../components/ui/RankBadge'
import Card from '../components/ui/Card'
import Button from '../components/ui/Button'
import type { Rank } from '../types'

const rankOrder: Rank[] = ['bronze', 'prata', 'ouro']

export default function ProfilePage() {
  const { slug } = useParams()
  const navigate = useNavigate()
  const currentIdx = rankOrder.indexOf(mockCurrentUser.rank)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [blockedUsers, setBlockedUsers] = useState<{ user_id: number; user_name: string }[]>([])
  const [unblocking, setUnblocking] = useState<number | null>(null)

  useEffect(() => {
    fetchBlocks()
  }, [])

  async function fetchBlocks() {
    try {
      const token = localStorage.getItem('token')
      const res = await fetch(apiUrl('/v1/community/my/blocks'), {
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
      })
      const data = await res.json()
      if (data.data) setBlockedUsers(data.data)
    } catch {}
  }

  async function handleUnblock(userId: number) {
    setUnblocking(userId)
    try {
      const token = localStorage.getItem('token')
      await fetch(apiUrl(`/v1/community/my/block/${userId}`), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
      })
      setBlockedUsers(prev => prev.filter(b => b.user_id !== userId))
    } catch {}
    setUnblocking(null)
  }

  const handleLogout = () => {
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    sispoliBridge.notifyLogout()
    navigate(`/${slug}/login`)
  }

  const handleDeleteAccount = async () => {
    setDeleting(true)
    // TODO: chamar API pra apagar conta
    await new Promise(r => setTimeout(r, 1000))
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    sispoliBridge.notifyLogout()
    navigate(`/${slug}/login`)
  }

  return (
    <div className="flex-1 overflow-y-auto">
      {/* Header — desktop only */}
      <div className="h-12 px-4 items-center border-b border-border-subtle hidden lg:flex">
        <h1 className="text-base font-semibold text-content-normal">Perfil</h1>
      </div>

      <div className="p-4 md:p-6 lg:p-8 max-w-5xl mx-auto w-full space-y-6">
        {/* Profile Card */}
        <Card className="!bg-gradient-to-br from-primary/20 via-surface-secondary to-surface-secondary">
          <div className="flex flex-col items-center text-center py-4">
            <Avatar name={mockCurrentUser.name} size="lg" rank={mockCurrentUser.rank} isOnline />
            <h2 className="text-xl font-bold text-content-normal mt-3">{mockCurrentUser.name}</h2>
            <RankBadge rank={mockCurrentUser.rank} size="md" className="mt-2" />

            <div className="grid grid-cols-3 gap-6 mt-6 w-full max-w-sm">
              <div>
                <p className="text-2xl font-bold text-content-normal">{mockCurrentUser.missionsCompleted}</p>
                <p className="text-xs text-content-muted flex items-center justify-center gap-1"><Target size={12} /> Missões</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-content-normal">{mockCurrentUser.totalPoints}</p>
                <p className="text-xs text-content-muted flex items-center justify-center gap-1"><Trophy size={12} /> Pontos</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-content-normal">{mockCurrentUser.streakDays}</p>
                <p className="text-xs text-content-muted flex items-center justify-center gap-1"><Flame size={12} /> Streak</p>
              </div>
            </div>
          </div>
        </Card>

        {/* Rank Progress */}
        <div>
          <h3 className="text-sm font-semibold text-content-muted uppercase tracking-wide mb-3 flex items-center gap-2">
            <Award size={16} /> Progressão de Rank
          </h3>
          <Card>
            <div className="flex items-center gap-1 overflow-x-auto pb-2">
              {rankOrder.map((rank, i) => {
                const config = RANK_CONFIG[rank]
                const isUnlocked = i <= currentIdx
                const isCurrent = rank === mockCurrentUser.rank
                return (
                  <div key={rank} className="flex items-center">
                    <div
                      className={`flex flex-col items-center px-3 py-2 rounded-lg transition-all ${
                        isCurrent ? 'bg-primary/10 scale-105' : ''
                      }`}
                    >
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                          isUnlocked ? '' : 'opacity-30'
                        }`}
                        style={{ backgroundColor: `${config.color}30`, color: config.color }}
                      >
                        {i + 1}
                      </div>
                      <span className={`text-[10px] mt-1 font-medium ${isUnlocked ? 'text-content-normal' : 'text-content-muted opacity-50'}`}>
                        {config.label}
                      </span>
                    </div>
                    {i < rankOrder.length - 1 && (
                      <div className={`w-4 h-0.5 ${i < currentIdx ? 'bg-primary' : 'bg-border-subtle'}`} />
                    )}
                  </div>
                )
              })}
            </div>
          </Card>
        </div>

        {/* Badges */}
        <div>
          <h3 className="text-sm font-semibold text-content-muted uppercase tracking-wide mb-3 flex items-center gap-2">
            <Calendar size={16} /> Conquistas
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {mockBadges.map((badge) => {
              const iconMap: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
                Rocket, Flame, Medal, Zap, Users, Crown, Crosshair, Award,
              }
              const IconComponent = iconMap[badge.icon] || Award
              return (
                <Card
                  key={badge.id}
                  className={`flex flex-col items-center text-center py-4 ${!badge.earned ? 'opacity-30' : ''}`}
                >
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center mb-2"
                    style={{ backgroundColor: `${badge.color}20`, color: badge.color }}
                  >
                    <IconComponent size={24} />
                  </div>
                  <p className="text-sm font-semibold text-content-normal">{badge.name}</p>
                  <p className="text-xs text-content-muted mt-1">{badge.description}</p>
                  {badge.earned && badge.earnedAt && (
                    <p className="text-[10px] text-content-muted mt-2">
                      {new Date(badge.earnedAt).toLocaleDateString('pt-BR')}
                    </p>
                  )}
                </Card>
              )
            })}
          </div>
        </div>

        {/* Usuários bloqueados */}
        {blockedUsers.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-content-muted uppercase tracking-wide mb-3 flex items-center gap-2">
              <Ban size={16} /> Usuários bloqueados
            </h3>
            <Card>
              <div className="divide-y divide-border-subtle">
                {blockedUsers.map(b => (
                  <div key={b.user_id} className="flex items-center justify-between py-3 first:pt-0 last:pb-0">
                    <div className="flex items-center gap-3">
                      <Avatar name={b.user_name} size="sm" />
                      <span className="text-sm text-content-normal">{b.user_name}</span>
                    </div>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => handleUnblock(b.user_id)}
                      loading={unblocking === b.user_id}
                    >
                      Desbloquear
                    </Button>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}

        {/* Ações da conta */}
        <div className="space-y-3 pt-4 border-t border-border-subtle">
          <Button
            fullWidth
            variant="secondary"
            onClick={handleLogout}
            icon={<LogOut size={16} />}
          >
            Sair da conta
          </Button>

          <button
            onClick={() => setShowDeleteModal(true)}
            className="w-full py-2.5 text-sm font-medium text-status-danger hover:bg-status-danger/10 rounded-xl transition-colors flex items-center justify-center gap-2"
          >
            <Trash2 size={16} />
            Apagar minha conta
          </button>
        </div>
      </div>

      {/* Modal apagar conta */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowDeleteModal(false)} />
          <div className="relative bg-surface-primary rounded-2xl w-full max-w-sm p-6 shadow-xl animate-scale-in">
            <div className="flex flex-col items-center text-center">
              <div className="w-14 h-14 rounded-full bg-status-danger/10 flex items-center justify-center mb-4">
                <AlertTriangle size={28} className="text-status-danger" />
              </div>
              <h3 className="text-lg font-bold text-content-normal mb-2">Apagar conta</h3>
              <p className="text-sm text-content-muted mb-1">
                Tem certeza que deseja apagar sua conta?
              </p>
              <p className="text-sm text-content-muted mb-6">
                Todos os seus dados serão excluídos permanentemente e você <strong className="text-content-normal">não poderá mais acessar</strong> esta comunidade.
              </p>

              <div className="w-full space-y-2">
                <button
                  onClick={handleDeleteAccount}
                  disabled={deleting}
                  className="w-full py-3 rounded-xl bg-status-danger text-white text-sm font-semibold hover:brightness-90 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {deleting ? (
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <Trash2 size={16} />
                  )}
                  {deleting ? 'Apagando...' : 'Sim, apagar minha conta'}
                </button>
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="w-full py-3 rounded-xl text-sm font-medium text-content-muted hover:bg-surface-hover transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
